import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import {
  canSeeOrders,
  projectsHome,
  useOrgScope,
  visibleSettingsSections,
} from '../../entities/org';
import {
  type NavItem,
  ordersNavItem,
  settingsNavItem,
  settingsSectionNavItem,
  workspaceNavItem,
} from './nav-config';
import { OrgSwitcher } from './org-switcher';
import { AppShell } from './ui';
import { WorkspaceNavFooter } from './workspace-nav-footer';

/**
 * The app shell fed from the org scope. Two modes, told apart by the URL alone:
 * - projects: the active org's workspaces (archived ones behind a toggle, a search when there are several,
 *   "New workspace" for `workspaces.manage`), with Orders (for someone who can see or pay orders) and Settings
 *   at the foot;
 * - settings (`/o/<org>/settings/...`): "Back to projects" and the settings sections the caller may see.
 * The org switcher sits at the very bottom in both.
 */
export function SignedInShell({
  userEmail,
  userName,
  extraBottomItems = [],
  children,
}: {
  userEmail: string;
  userName?: { firstName: string; lastName: string };
  /** After Settings. `RequireAuth` adds the staff area for staff, and the design reference in dev builds. */
  extraBottomItems?: NavItem[];
  children: ReactNode;
}) {
  const { t } = useTranslation(['workspace', 'org', 'shell']);
  const scope = useOrgScope();
  const { pathname } = useLocation();
  const [showArchived, setShowArchived] = useState(false);
  const ready = scope.status === 'ready';
  const org = ready ? scope.shellOrg : null;
  const shared = {
    userEmail,
    ...(userName ? { userName } : {}),
    switcherSlot: (context: Parameters<typeof OrgSwitcher>[0]) => <OrgSwitcher {...context} />,
  };

  const settingsBase = org ? `/o/${org.slug}/settings` : null;
  const inSettings =
    ready &&
    !scope.notFound &&
    settingsBase !== null &&
    (pathname === settingsBase || pathname.startsWith(`${settingsBase}/`));

  if (ready && org && inSettings) {
    return (
      <AppShell
        {...shared}
        navLabel={t('org:settings.title')}
        navItems={visibleSettingsSections(scope).map((section) =>
          settingsSectionNavItem(org.slug, section, t(`org:settings.tabs.${section}`)),
        )}
        backLink={{
          label: t('shell:nav.backToProjects'),
          to: projectsHome(org, scope.activeWorkspaces),
        }}
        bottomItems={extraBottomItems}
      >
        {children}
      </AppShell>
    );
  }

  const workspaces =
    ready && org
      ? [...scope.activeWorkspaces, ...(showArchived ? scope.archivedWorkspaces : [])]
      : [];
  return (
    <AppShell
      {...shared}
      navLabel={t('nav.label')}
      navItems={workspaces.map((workspace) => workspaceNavItem(org?.slug ?? '', workspace))}
      {...(ready && scope.activeWorkspaces.length > 1
        ? { searchLabel: t('shell:nav.searchWorkspaces') }
        : {})}
      bottomItems={[
        ...(ready && org && !scope.notFound && canSeeOrders(scope)
          ? [ordersNavItem(org.slug, t('shell:nav.orders'))]
          : []),
        ...(org ? [settingsNavItem(org.slug, t('shell:nav.settings'))] : []),
        ...extraBottomItems,
      ]}
      {...(ready && org
        ? {
            navFooterSlot: (context) => (
              <WorkspaceNavFooter
                {...context}
                org={org}
                archivedCount={scope.archivedWorkspaces.length}
                showArchived={showArchived}
                onToggleArchived={() => setShowArchived((current) => !current)}
                canCreate={scope.can('workspaces.manage')}
              />
            ),
          }
        : {})}
    >
      {children}
    </AppShell>
  );
}
